import axios from "axios";
import Rollbar from "rollbar";

import { supabaseClient } from "./supabaseClient";
import { rollbarConfig } from "./rollbarConfig";

// In dev, route through the Vite proxy ("/api") to avoid CORS; in prod, hit the
// backend directly. The "/api" prefix is stripped by the proxy rewrite (vite.config.ts).
const API_URL = import.meta.env.DEV ? "/api" : (import.meta.env.VITE_API_URL as string);
const axiosInstance = axios.create({
	baseURL: API_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

const CHAT_API_URL = import.meta.env.DEV ? "/chat-api" : (import.meta.env.VITE_CHAT_API_URL as string);
const chatApiInstance = axios.create({
	baseURL: CHAT_API_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

// Initialize Rollbar instance for API error reporting
const rollbar = new Rollbar(rollbarConfig);

// Request interceptor - Add auth token
axiosInstance.interceptors.request.use(async (config) => {
	const {
		data: { session },
	} = await supabaseClient.auth.getSession();
	if (session?.access_token) {
		config.headers.Authorization = `Bearer ${session.access_token}`;
	}
	return config;
});

chatApiInstance.interceptors.request.use(async (config) => {
	const {
		data: { session },
	} = await supabaseClient.auth.getSession();
	if (session?.access_token) {
		config.headers.Authorization = `Bearer ${session.access_token}`;
	}
	return config;
});

// Response interceptor - Report server errors to Rollbar
axiosInstance.interceptors.response.use(
	(response) => response,
	(error) => {
		if (error.response) {
			const status = error.response.status;

			// Only report actual server errors (5xx) to Rollbar
			// Explicitly exclude validation errors (422) and all client errors (4xx)
			const isServerError = status >= 500;
			const isValidationError = status === 422;

			if (isServerError) {
				rollbar.error("API Server Error", {
					url: error.config?.url,
					method: error.config?.method?.toUpperCase(),
					status: error.response.status,
					statusText: error.response.statusText,
					responseData: error.response.data,
					requestData: error.config?.data,
					timestamp: new Date().toISOString(),
				});
			}

			// Log validation errors to console in development only
			if (isValidationError && import.meta.env.DEV) {
				console.warn("[API Validation Error]", {
					url: error.config?.url,
					method: error.config?.method?.toUpperCase(),
					errors: error.response.data,
				});
			}
		}

		// Always reject so calling code can handle the error
		return Promise.reject(error);
	},
);

export { axiosInstance, chatApiInstance, API_URL, CHAT_API_URL };
